import React,
{
  useEffect,
  useState,
  useRef
} from "react";
import Select from "react-select";
import { notify } from "../App";

// Empty string / invalid -> 0 (never NaN)
const num = (v) => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const Rental = () => {

  const [products, setProducts] = useState([]);
  const productOptions = products.map((p) => ({
  value: p.id,
  label: `${p.name} (Available: ${p.available_quantity})`
}));

  // =========================
  // ✅ CAMERA STATES
  // =========================

  const [cameraOpen, setCameraOpen] =
    useState(false);

  const [photoFile, setPhotoFile] =
    useState(null);

  const videoRef = useRef(null);

  const canvasRef = useRef(null);

  // =========================
  // ✅ FORM
  // =========================

  const [form, setForm] = useState({

    customer: {
      first_name: "",
      last_name: "",
      phone: ""
    },

    address: "",

    shipping_address: "",

    note: "",

    products: [],

    rent_date: "",

    return_date: "",

    deposit: "",

    labour_charges: "",

    transport_charges: "",

    rental_type: "regular"

  });

  // =========================
  // ✅ FETCH PRODUCTS
  // =========================

  useEffect(() => {

    fetch("/api/products")
      .then(res => res.json())
      .then(data =>
        setProducts(
          data.products || data
        )
      );

  }, []);

  // =========================
  // ✅ CUSTOMER CHANGE
  // =========================

  const handleCustomerChange = (e) => {

    setForm({

      ...form,

      customer: {
        ...form.customer,
        [e.target.name]:
          e.target.value
      }

    });
  };

  // =========================
  // ✅ ADD PRODUCT
  // =========================

  const addProduct = () => {

    setForm({

      ...form,

      products: [
        ...form.products,

        {
          product_id: "",
          quantity: 1,
          rent: 0
        }
      ]

    });
  };

const removeProduct = (index) => {

  const updated = [...form.products];

  updated.splice(index, 1);

  setForm({
    ...form,
    products: updated
  });

};
  // =========================
  // ✅ PRODUCT CHANGE
  // =========================

  const handleProductChange = (
    index,
    field,
    value
  ) => {

    const updated =
      [...form.products];

    // =========================
    // DUPLICATE CHECK
    // =========================

    if (field === "product_id") {

      const exists =
        updated.find((p, i) =>
          p.product_id === value &&
          i !== index
        );

      if (exists) {
        notify("Product already added", "error");
        return;
      }

      const selected =
        products.find(
          p => p.id == value
        );

      if (selected) {

        updated[index].rent =
          Number(
            selected.rent_per_day
          ) || 0;
      }
    }

    // =========================
    // STOCK CHECK
    // =========================

    if (field === "quantity") {

      const product =
        products.find(
          p =>
            p.id ==
            updated[index].product_id
        );

      if (
        product &&
        value >
        product.available_quantity
      ) {
        notify(
          `Only ${product.available_quantity} items available`,
          "error"
        );

        return;
      }
    }

    updated[index][field] = value;

    setForm({
      ...form,
      products: updated
    });
  };

  // =========================
  // ✅ START CAMERA
  // =========================

  const startCamera = async () => {

    try {

      const stream =
        await navigator
          .mediaDevices
          .getUserMedia({
            video: true,
            audio: false
          });

      setCameraOpen(true);

      setTimeout(() => {

        if (videoRef.current) {

          videoRef.current.srcObject =
            stream;

          videoRef.current.play();
        }

      }, 200);

    } catch (err) {

      console.error(err);

      notify("Camera access denied", "error");
    }
  };

  // =========================
  // ✅ CAPTURE PHOTO
  // =========================

  const capturePhoto = () => {

    const video =
      videoRef.current;

    const canvas =
      canvasRef.current;

    if (!video || !canvas) {

      notify("Camera not ready", "error");

      return;
    }

    const stream =
      video.srcObject;

    if (!stream) {

      notify("Camera stream not found", "error");

      return;
    }

    const context =
      canvas.getContext("2d");

    canvas.width =
      video.videoWidth;

    canvas.height =
      video.videoHeight;

    context.drawImage(
      video,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {

      if (!blob) {

        notify("Photo capture failed", "error");

        return;
      }

      const file = new File(
        [blob],
        `customer_${Date.now()}.png`,
        {
          type: "image/png"
        }
      );

      setPhotoFile(file);

    }, "image/png");

    // =========================
    // STOP CAMERA
    // =========================

    stream
      .getTracks()
      .forEach(track =>
        track.stop()
      );

    video.srcObject = null;

    setCameraOpen(false);
  };

  // =========================
  // ✅ DAYS
  // =========================

  const getDays = () => {

    if (
      !form.rent_date ||
      !form.return_date
    ) {
      return 1;
    }

    const start =
      new Date(form.rent_date);

    const end =
      new Date(form.return_date);

    const diff =
      Math.ceil(
        (end - start) /
        (1000 * 60 * 60 * 24)
      ) + 1;

    return diff > 0
      ? diff
      : 1;
  };

  const days = getDays();

  // =========================
  // ✅ TOTAL RENT
  // =========================

  const totalAmount =
    form.products.reduce(
      (sum, item) => {

        const rent =
          Number(item.rent) || 0;

        const qty =
          Number(item.quantity) || 0;

        return (
          sum +
          (rent * qty * days)
        );

      },
      0
    );

  const deposit =
    num(form.deposit);

  const labourCharges =
    num(form.labour_charges);

  const transportCharges =
    num(form.transport_charges);

  // =========================
  // ✅ GRAND COLLECTED
  // =========================

  const grandCollected =

    form.rental_type === "credit"

      ? labourCharges +
        transportCharges

      : totalAmount +
        deposit +
        labourCharges +
        transportCharges;

  // =========================
  // ✅ PENDING AMOUNT
  // =========================

  const pendingAmount =

    form.rental_type === "credit"

      ? totalAmount +
        labourCharges +
        transportCharges

      : 0;

  // =========================
  // ✅ SUBMIT
  // =========================

  const handleSubmit = async () => {

    if (
      !form.customer.first_name ||
      !form.customer.phone
    ) {
      notify(
        "Customer name and phone required",
        "error"
      );

      return;
    }

    if (
      form.products.length === 0
    ) {
      notify(
        "Add at least one product",
        "error"
      );

      return;
    }

    if (
      !form.rent_date ||
      !form.return_date
    ) {
      notify("Select dates", "error");

      return;
    }

    try {

      const formData =
        new FormData();

      formData.append(
        "data",
        JSON.stringify(form)
      );

      if (photoFile) {

        formData.append(
          "customer_photo",
          photoFile
        );
      }

      const res =
        await fetch(
          "/api/create-rental",
          {
            method: "POST",
            body: formData
          }
        );

      const data =
        await res.json();

      notify(
        data.message,
        res.ok ? "success" : "error"
      );

      if (!res.ok) {
        return;
      }

      setForm({

  customer: {
    first_name: "",
    last_name: "",
    phone: ""
  },

  address: "",

  shipping_address: "",

  note: "",

  products: [],

  rent_date: "",

  return_date: "",

  deposit: 0,

  labour_charges: 0,

  transport_charges: 0,

  rental_type: "regular"
});

setPhotoFile(null);

    } catch (err) {

      console.error(err);

      notify(
        "Error creating rental",
        "error"
      );
    }
  };
// =========================
// ✅ SAFE NUMBER INPUT
// =========================

const preventInvalidNumberInput = (e) => {

  if (
    e.key === "-" ||
    e.key === "+" ||
    e.key === "e" ||
    e.key === "E" ||
    e.key === "ArrowUp" ||
    e.key === "ArrowDown"
  ) {
    e.preventDefault();
  }
};
  return (

 <div className="min-h-screen bg-[#F5F7FB] p-4 md:p-6">

    {/* ========================================= */}
    {/* HERO */}
    {/* ========================================= */}

   <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-5 md:p-8 text-white shadow-xl mb-6">

      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-6">

        <div>

          <p className="uppercase tracking-widest text-indigo-200 text-sm">

            Rental Management

          </p>

          <h1 className="text-2xl md:text-4xl font-bold mt-2">

            Create Rental

          </h1>

          <p className="mt-3 text-indigo-100">

            Manage customer rentals,
            product allocation and billing.

          </p>

        </div>

        <div className="hidden lg:flex gap-4">

          <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">

            <p className="text-sm text-indigo-100">
              Products
            </p>

            <h2 className="text-3xl font-bold mt-1">

              {form.products.length}

            </h2>

          </div>

          <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">

            <p className="text-sm text-indigo-100">
              Total Amount
            </p>

            <h2 className="text-3xl font-bold mt-1">

              ₹{totalAmount}

            </h2>

          </div>

        </div>

      </div>

    </div>

    {/* ========================================= */}
    {/* MAIN GRID */}
    {/* ========================================= */}

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

      {/* ========================================= */}
      {/* LEFT SECTION */}
      {/* ========================================= */}

      <div className="col-span-1 lg:col-span-8 space-y-6">

        {/* ========================================= */}
        {/* CUSTOMER DETAILS */}
        {/* ========================================= */}

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 md:p-8">

          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">

            <div>

              <h2 className="text-2xl font-bold text-gray-800">

                Customer Details

              </h2>

              <p className="text-sm text-gray-500 mt-1">

                Enter customer information

              </p>

            </div>

            <div className="bg-indigo-100 text-indigo-600 px-4 py-2 rounded-xl text-sm font-semibold">

              Customer Info

            </div>

          </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <input
              name="first_name"
              placeholder="First Name"
              onChange={handleCustomerChange}
              className="border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <input
              name="last_name"
              placeholder="Last Name"
              onChange={handleCustomerChange}
              className="border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <input
              name="phone"
              placeholder="Phone Number"
              onChange={handleCustomerChange}
              className="border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <input
              placeholder="Address"
              onChange={(e) =>
                setForm({
                  ...form,
                  address: e.target.value
                })
              }
              className="border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <input
              placeholder="Shipping Address"
              onChange={(e) =>
                setForm({
                  ...form,
                  shipping_address:
                    e.target.value
                })
              }
              className="md:col-span-2 border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <textarea
              placeholder="Note"
              rows="3"
              onChange={(e) =>
                setForm({
                  ...form,
                  note: e.target.value
                })
              }
              className="md:col-span-2 border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />

          </div>

          {/* PHOTO */}

          <div className="mt-8 border-t pt-6">

            <h3 className="font-semibold text-gray-700 mb-4">

              Customer Photo

            </h3>

            {!cameraOpen &&
              !photoFile && (

              <button
                type="button"
                onClick={startCamera}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-2xl font-medium hover:shadow-lg transition"
              >

                Capture Photo

              </button>

            )}

            {cameraOpen && (

              <div className="space-y-4">

                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full max-w-xl rounded-2xl border bg-black"
                />

                <div className="flex flex-col sm:flex-row gap-4">

                  <button
                    type="button"
                    onClick={capturePhoto}
                    className="bg-green-600 text-white px-5 py-3 rounded-2xl"
                  >
                    Take Photo
                  </button>

                  <button
                    type="button"
                    onClick={() => {

                      const stream =
                        videoRef.current?.srcObject;

                      if (stream) {

                        stream
                          .getTracks()
                          .forEach(track =>
                            track.stop()
                          );
                      }

                      setCameraOpen(false);

                    }}
                    className="bg-red-500 text-white px-5 py-3 rounded-2xl"
                  >
                    Close Camera
                  </button>

                </div>

              </div>

            )}

            {photoFile && (

              <div className="space-y-4">

                <img
                  src={URL.createObjectURL(photoFile)}
                  alt="Customer"
                  className="w-56 h-56 object-cover rounded-3xl border"
                />
<div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={() => {

                      setPhotoFile(null);

                      startCamera();

                    }}
                    className="border border-gray-200 px-5 py-3 rounded-2xl hover:bg-gray-100"
                  >
                    Retake
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setPhotoFile(null)
                    }
                    className="bg-red-500 text-white px-5 py-3 rounded-2xl"
                  >
                    Remove
                  </button>

                </div>

              </div>

            )}

            <canvas
              ref={canvasRef}
              className="hidden"
            />

          </div>

        </div>

        {/* ========================================= */}
        {/* PRODUCTS */}
        {/* ========================================= */}

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 md:p-8">

        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">

            <div>

              <h2 className="text-2xl font-bold text-gray-800">

                Rental Products

              </h2>

              <p className="text-sm text-gray-500 mt-1">

                Select products for rental

              </p>

            </div>

            <button
              onClick={addProduct}
              className="whitespace-nowrap bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 rounded-2xl font-medium hover:shadow-lg transition"
            >

              + Add Product

            </button>

          </div>

          <div className="space-y-5">

            {form.products.map(
              (item, index) => (

              <div
                key={index}
                className="border border-gray-200 rounded-3xl p-4 md:p-6 bg-gray-50"
              >
<div className="flex justify-between items-center mb-5">

  <h3 className="font-semibold text-gray-700">
    Product {index + 1}
  </h3>

  <button
    type="button"
    onClick={() => removeProduct(index)}
    className="text-red-600 hover:text-red-700 font-semibold"
  >
    🗑 Remove
  </button>

</div>
             <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
<div className="md:col-span-6">

                    <label className="text-sm text-gray-500 block mb-2">

                      Product

                    </label>

                    <Select
  options={productOptions}
  placeholder="Search Product..."
  value={
    productOptions.find(
      (option) => option.value === item.product_id
    ) || null
  }
  onChange={(selectedOption) =>
    handleProductChange(
      index,
      "product_id",
      selectedOption.value
    )
  }
  isSearchable
/>

                  </div>

                <div className="md:col-span-2">

                    <label className="text-sm text-gray-500 block mb-2">

                      Qty

                    </label>

                    <input
                      type="number"
                      min="0"

onKeyDown={preventInvalidNumberInput}

onWheel={(e) => e.target.blur()}
                      value={item.quantity}
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "quantity",
                          Number(e.target.value)
                        )
                      }
                      className="w-full border border-gray-200 bg-white rounded-2xl px-4 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
                    />

                  </div>

                  <div className="md:col-span-2">

                    <label className="text-sm text-gray-500 block mb-2">

                      Rent/Day

                    </label>

                    <div className="bg-indigo-50 text-indigo-700 font-semibold rounded-2xl px-4 py-4 text-center">

                      ₹{item.rent}

                    </div>

                  </div>

                  <div className="md:col-span-2">

                    <label className="text-sm text-gray-500 block mb-2">

                      Total

                    </label>

                    <div className="bg-green-50 text-green-700 font-bold rounded-2xl px-4 py-4 text-center">

                      ₹{
                        (item.rent || 0) *
                        (item.quantity || 0) *
                        days
                      }

                    </div>

                  </div>

                </div>

              </div>

            ))}

          </div>

        </div>

        {/* ========================================= */}
        {/* DATES */}
        {/* ========================================= */}

       <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 md:p-8">

        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">

            <div>

              <h2 className="text-2xl font-bold text-gray-800">

                Rental Duration

              </h2>

              <p className="text-sm text-gray-500 mt-1">

                Select rental dates

              </p>

            </div>

            <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-xl text-sm font-semibold">

              {days}
              {" "}
              Days

            </div>

          </div>

       <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <div>

              <label className="text-sm text-gray-500 block mb-2">

                Rent Date

              </label>

             <input
  type="date"

  value={form.rent_date}

  onChange={(e) =>
    setForm({
      ...form,
      rent_date: e.target.value
    })
  }

  className="w-full border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
/>

            </div>

            <div>

              <label className="text-sm text-gray-500 block mb-2">

                Return Date

              </label>

              <input
  type="date"

  value={form.return_date}

  onChange={(e) =>
    setForm({
      ...form,
      return_date: e.target.value
    })
  }

  className="w-full border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
/>

            </div>

          </div>

        </div>

      </div>

      {/* ========================================= */}
      {/* BILLING */}
      {/* ========================================= */}

     <div className="col-span-1 lg:col-span-4">

       <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 md:p-8 lg:sticky lg:top-6">

          <div className="flex justify-between items-center mb-8">

            <div>

              <h2 className="text-2xl font-bold text-gray-800">

                Billing Summary

              </h2>

              <p className="text-sm text-gray-500 mt-1">

                Rental payment details

              </p>

            </div>

          </div>

          {/* RENTAL TYPE */}

          <div className="grid grid-cols-2 gap-4 mb-6">

            <button
              onClick={() =>
                setForm({
                  ...form,
                  rental_type:
                    "regular"
                })
              }
              className={`p-4 rounded-2xl border text-sm font-semibold transition

                ${
                  form.rental_type ===
                  "regular"

                    ? "border-indigo-500 bg-indigo-50 text-indigo-700"

                    : "border-gray-200 bg-white text-gray-600"
                }
              `}
            >

              Regular

            </button>

            <button
              onClick={() =>
                setForm({
                  ...form,
                  rental_type:
                    "credit"
                })
              }
              className={`p-4 rounded-2xl border text-sm font-semibold transition

                ${
                  form.rental_type ===
                  "credit"

                    ? "border-red-500 bg-red-50 text-red-700"

                    : "border-gray-200 bg-white text-gray-600"
                }
              `}
            >

              Credit

            </button>

          </div>

          {/* BILLING DETAILS */}

          <div className="space-y-5">

            <div className="flex justify-between text-gray-700">

              <span>Rent Total</span>

              <span className="font-semibold">

                ₹{totalAmount}

              </span>

            </div>

            {/* DEPOSIT */}

            {form.rental_type ===
              "regular" && (

              <div>

                <label className="text-sm text-gray-500 block mb-2">

                  Deposit

                </label>

                <input
                  type="number"
                  min="0"

onKeyDown={preventInvalidNumberInput}

onWheel={(e) => e.target.blur()}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      deposit:
                        Number(
                          e.target.value
                        )
                    })
                  }
                  className="w-full border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
                />

              </div>

            )}

            {/* LABOUR */}

            <div>

              <label className="text-sm text-gray-500 block mb-2">

                Labour Charges

              </label>

              <input
                type="number"
                min="0"

onKeyDown={preventInvalidNumberInput}

onWheel={(e) => e.target.blur()}
                value={
                  form.labour_charges
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    labour_charges:
                      Number(
                        e.target.value
                      )
                  })
                }
                className="w-full border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
              />

            </div>

            {/* TRANSPORT */}

            <div>

              <label className="text-sm text-gray-500 block mb-2">

                Transport Charges

              </label>

              <input
                type="number"
                min="0"

onKeyDown={preventInvalidNumberInput}

onWheel={(e) => e.target.blur()}
                value={
                  form.transport_charges
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    transport_charges:
                      Number(
                        e.target.value
                      )
                  })
                }
                className="w-full border border-gray-200 bg-gray-50 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-indigo-500"
              />

            </div>

            {/* TOTAL */}

            <div className="border-t pt-5 space-y-4">

              <div className="flex justify-between text-lg font-bold text-green-600">

                <span>

                  {

                    form.rental_type ===
                    "credit"

                      ? "Extra Charges"

                      : "Grand Collected"
                  }

                </span>

                <span>
                  ₹{grandCollected}
                </span>

              </div>

              {form.rental_type ===
                "credit" && (

                <div className="flex justify-between text-lg font-bold text-red-600">

                  <span>
                    Pending Amount
                  </span>

                  <span>
                    ₹{pendingAmount}
                  </span>

                </div>

              )}

            </div>

          </div>

          {/* BUTTON */}

          <button
            onClick={handleSubmit}
            className="w-full mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-semibold text-lg hover:shadow-xl transition"
          >

            Create Rental

          </button>

        </div>

      </div>

    </div>

  </div>

);
};

export default Rental;